import { Box, Typography, Chip, Skeleton } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

// Thresholds
const SENSOR_CONFIG = {
  airTemperatureC: {
    label: "Air Temperature",
    unit: "°C",
    decimals: 1,
    normalMin: 0,
    normalMax: 40,
    warningMax: 40,
    criticalMax: 45,
  },

  processTemperatureC: {
    label: "Process Temperature",
    unit: "°C",
    decimals: 1,
    normalMin: 0,
    normalMax: 80,
    warningMax: 80,
    criticalMax: 100,
  },

  rotationalSpeed: {
    label: "Rotational Speed",
    unit: "RPM",
    decimals: 0,
    normalMin: 600,
    normalMax: 3000,
    warningMax: 3000,
    criticalMax: 3500,
  },

  torque: {
    label: "Torque",
    unit: "N·m",
    decimals: 1,
    normalMin: 0,
    normalMax: 75,
    warningMax: 75,
    criticalMax: 110,
  },

  toolWear: {
    label: "Tool Wear",
    unit: "min",
    decimals: 0,
    normalMin: 0,
    normalMax: 150,
    warningMax: 150,
    criticalMax: 230,
  },
};

const statusConfig = {
  Critical: { color: "#DC2626", bg: "#FEE2E2", border: "#FECACA" },
  Warning: { color: "#D97706", bg: "#FEF3C7", border: "#FDE68A" },
  Healthy: { color: "#16A34A", bg: "#DCFCE7", border: "#BBF7D0" },
};

const getStatus = (value, config) => {
  if (value == null) return "Healthy";
  if (value >= config.criticalMax || value < config.normalMin)
    return "Critical";
  if (value >= config.warningMax) return "Warning";
  return "Healthy";
};


const mapSensors = (data) => {
  if (!data) return [];

  return Object.entries(SENSOR_CONFIG)
    .map(([key, config]) => {
      const rawValue = data[key] ?? (key === "rotationalSpeed" ? data["roationalSpeed"] : undefined);

      const formattedValue =
        rawValue != null && rawValue > 0
          ? Number(rawValue).toLocaleString(undefined, {
              maximumFractionDigits: config.decimals ?? 2,
            })
          : "NA";

      const status = getStatus(rawValue, config);

      return {
        id: key,
        label: config.label,
        unit: config.unit,
        value: formattedValue,
        status,
        normalRange: `${config.normalMin}-${config.normalMax} ${config.unit}`,
        warningThreshold: `> ${config.warningMax} ${config.unit}`,
        criticalThreshold: `> ${config.criticalMax} ${config.unit}`,
      };
    })
    .filter((sensor) => sensor.value !== "NA"); 
};

const ConnectedSensors = ({ sensors }) => {
  const mapped = mapSensors(sensors);

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: "20px",
            lineHeight: "28px",
            color: "#020618",
          }}
        >
          Connected Sensors
        </Typography>
      </Box>

      {/* Skeleton while loading */}
      {!sensors ? (
        <Box sx={{ display: "flex", gap: 2.5 }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                p: 2,
                borderRadius: 2,
                border: "1px solid #E5E7EB",
              }}
            >
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton variant="text" width="40%" height={48} sx={{ my: 1 }} />
              <Skeleton variant="text" width="80%" height={16} />
            </Box>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(5, 1fr)",
            },
            gap: 2,
          }}
        >
          {mapped.map((sensor) => {
            const config = statusConfig[sensor.status];
            return (
              <Box
                key={sensor.id}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid #E5E7EB",
                }}
              >
                {/* Label + Status chip */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 1.5,
                  }}
                >
                  <Typography sx={{ fontSize: "0.8125rem", color: "#6B7280" }}>
                    {sensor.label}
                  </Typography>
                  <Chip
                    label={sensor.status}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: "0.6875rem",
                      fontWeight: 600,
                      bgcolor: config.bg,
                      color: config.color,
                      border: `1px solid ${config.border}`,
                    }}
                  />
                </Box>

                {/* Value */}
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.75rem",
                    color: "#1B2A3D",
                    mb: 1,
                  }}
                >
                  {sensor.value}
                  <Box
                    component="span"
                    sx={{ fontSize: "1rem", fontWeight: 500, ml: 0.25 }}
                  >
                    {sensor.value !== "N/A" ? sensor.unit : ""}
                  </Box>
                </Typography>

                {/* Normal range */}
                <Typography
                  sx={{ fontSize: "0.75rem", color: "#6B7280", mb: 1 }}
                >
                  Normal : {sensor.normalRange}
                </Typography>

                {/* Warning + Critical thresholds */}
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <WarningAmberIcon sx={{ fontSize: 13, color: "#D97706" }} />
                    <Typography
                      sx={{
                        fontSize: "0.6875rem",
                        color: "#D97706",
                        fontWeight: 500,
                      }}
                    >
                      Warning : {sensor.warningThreshold}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <ErrorOutlineIcon sx={{ fontSize: 13, color: "#DC2626" }} />
                    <Typography
                      sx={{
                        fontSize: "0.6875rem",
                        color: "#DC2626",
                        fontWeight: 500,
                      }}
                    >
                      Critical : {sensor.criticalThreshold}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ConnectedSensors;
